import { effect, inject, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { AppError, SpinnerService } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, switchMap, tap } from 'rxjs';

import {
  mapWithCoins,
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '@kr/core';
import { WalletCard } from '@kr/models';
import { DictionariesStore } from '@kr/store';

import { WALLET_DETAILS_API } from './api';
import { WalletEditDialog } from './components/wallet-edit/wallet-edit.dialog';

interface WalletDetailsState {
  walletEdited: boolean;
  wallet: WalletCard;
}

const initialState: WalletDetailsState = {
  wallet: null,
  walletEdited: false,
};

export const WalletDetailsStore = signalStore(
  withState(initialState),
  withRequestStatus(['getWalletDetails', 'editWallet', 'deleteWallet']),
  withRequestHandler(),
  withProps(store => ({
    store,
    walletDetailsApi: inject(WALLET_DETAILS_API),
    storeHelperService: inject(StoreHelperService),
    spinnerService: inject(SpinnerService),
    walletEditDialog: inject(WalletEditDialog),
    dictionariesStore: inject(DictionariesStore),
  })),
  withMethods(({ store, walletDetailsApi }) => ({
    editWallet: rxMethod<WalletCard>(
      switchMap(wallet => {
        patchState(store, setPendingStatusFor('editWallet'));
        return walletDetailsApi.editWallet(wallet).pipe(
          tapResponse({
            next: () => {
              patchState(store, setSuccessStatusFor('editWallet'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('editWallet', error)),
          }),
        );
      }),
    ),
    deleteWallet: rxMethod<WalletCard>(
      switchMap(wallet => {
        patchState(store, setPendingStatusFor('deleteWallet'));
        return walletDetailsApi.deleteWallet(wallet).pipe(
          tapResponse({
            next: () => patchState(store, setSuccessStatusFor('deleteWallet')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('deleteWallet', error)),
          }),
        );
      }),
    ),
  })),
  withMethods(({ store, walletDetailsApi, walletEditDialog, editWallet, dictionariesStore, deleteWallet }) => ({
    getWalletDetails: rxMethod<WalletCard>(
      switchMap(wallet => {
        patchState(store, setPendingStatusFor('getWalletDetails'));
        return walletDetailsApi.getWalletDetails(wallet).pipe(
          tapResponse({
            next: wallet => {
              patchState(
                store,
                { wallet: mapWithCoins<WalletCard>(wallet, dictionariesStore.coins()) },
                setSuccessStatusFor('getWalletDetails'),
              );
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getWalletDetails', error)),
          }),
        );
      }),
    ),
    openWalletEditDialog: rxMethod<void>(
      switchMap(() => {
        return walletEditDialog
          .open(store.wallet())
          .afterClosed<any>()
          .pipe(
            filter(Boolean),
            tap(payload => {
              if (payload.isDeleted) {
                deleteWallet(payload);
                store.resetRequestStatus('deleteWallet');
              }
              patchState(store, { wallet: payload, walletEdited: !!payload });
              editWallet(payload);
            }),
          );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      store.spinnerService.observe(toObservable(store.isLoading));

      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);
